import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Copy, Wand2, Image as ImageIcon, Trash2, Loader2, History, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";

const PLATFORMS = [
  ["instagram_post", "Instagram post"],
  ["instagram_story", "Instagram story"],
  ["instagram_carousel", "Instagram carousel"],
  ["facebook_post", "Facebook post"],
  ["linkedin_post", "LinkedIn post"],
  ["tiktok_script", "TikTok / Reel script"],
  ["email", "Email campaign"],
];
const TONES = [
  ["professional", "Επιστημονικό / επαγγελματικό"],
  ["punchy", "Δυναμικό & σύντομο"],
  ["storytelling", "Storytelling"],
  ["educational", "Εκπαιδευτικό"],
  ["urgent", "Προσφορά με επείγον"],
];
const CARDS = [["ig-post", "IG post 1080×1080"], ["ig-story", "IG/FB story 1080×1920"], ["fb-post", "FB post 1200×630"]];

export default function Studio() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [targets, setTargets] = useState(null);
  const [form, setForm] = useState({ target: "", platform: "instagram_post", tone: "professional", lang: "el", notes: "" });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState("");
  const [cardName, setCardName] = useState("sportscience-card.jpg");
  const [cardKind, setCardKind] = useState("ig-post");
  const [history, setHistory] = useState([]);

  const isOwner = user?.role === "admin";

  useEffect(() => {
    if (!isOwner) return;
    api.get("/studio/targets").then((r) => setTargets(r.data)).catch(() => setTargets({ courses: [], offer: { active: false } }));
    api.get("/studio/history").then((r) => setHistory(r.data)).catch(() => {});
  }, [isOwner]);

  const offer = targets?.offer;
  const selected = useMemo(() => (targets?.courses || []).find((c) => c.slug === form.target), [targets, form.target]);

  const generate = async () => {
    setBusy(true);
    setCard("");
    try {
      const { data } = await api.post("/studio/generate", form);
      setOut(data);
      setHistory((h) => [data, ...h]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const makeCard = async () => {
    if (!out) return;
    setBusy(true);
    try {
      const { data } = await api.post("/studio/card", {
        kind: cardKind,
        badge: offer?.active ? `-${offer.discount}%` : "",
        headline: out.card_headline || out.hook,
        subtitle: out.card_sub || "",
        perks: (out.carousel || []).slice(0, 3).map((s) => s.title),
        cta: (out.ctas || [])[0] || "sportscience.academy",
        deadline: offer?.active ? `${offer.label} · ισχύει σήμερα` : "",
      });
      setCard(data.url);
      setCardName(data.filename || "sportscience-card.jpg");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} αντιγράφηκε`);
  };

  const removeItem = async (id) => {
    await api.delete(`/studio/history/${id}`);
    setHistory((h) => h.filter((x) => x.id !== id));
  };

  if (!isOwner) {
    return (
      <div className="max-w-xl mx-auto px-5 pt-24 pb-24 text-center" data-testid="studio-locked">
        <Lock className="w-8 h-8 text-[#1D4ED8] mx-auto" />
        <h1 className="font-heading font-black text-3xl mt-5 text-[#0B1B3A]">Social Studio</h1>
        <p className="text-sm text-[#475569] mt-3">Μόνο για τον ιδιοκτήτη της πλατφόρμας. Συνδέσου με τον admin λογαριασμό σου για να ξεκλειδώσεις τη γεννήτρια καμπανιών.</p>
        <a href="/login" className="btn-blue inline-flex mt-6" data-testid="studio-login">Σύνδεση</a>
      </div>
    );
  }

  const field = (label, node) => (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{label}</span>
      <div className="mt-1.5">{node}</div>
    </label>
  );

  const section = (title, body, copyText) => (
    <div className="card-dark p-5 mt-4" data-testid={`studio-out-${title}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{title}</p>
        {copyText && (
          <button onClick={() => copy(copyText, title)} className="p-2 rounded-full text-[#334155] hover:text-[#1D4ED8]" data-testid={`studio-copy-${title}`}>
            <Copy className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="mt-2 text-sm text-[#334155] whitespace-pre-line">{body}</div>
    </div>
  );

  return (
    <>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 pb-24" data-testid="studio-page">
        <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="panel-cream p-7 sm:p-10">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white bg-[#1D4ED8] rounded-full px-4 py-2">
            <Sparkles className="w-3.5 h-3.5" /> Owner only
          </span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-5 text-[#0B1B3A]" data-testid="studio-title">Social Studio</h1>
          <p className="text-base text-[#475569] mt-4 max-w-2xl">
            Παράγει έτοιμο copy και γραφιστικές κάρτες για κάθε μάθημα — captions, hashtags, carousel, story, CTA και email θέματα, στα Ελληνικά ή Αγγλικά.
          </p>
          {offer?.active && (
            <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#0B1B3A] bg-white border border-[#E2E8F0] rounded-full px-4 py-2" data-testid="studio-offer">
              Σήμερα: {offer.label} · −{offer.discount}%
            </p>
          )}
        </motion.section>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <div className="card-dark p-6 space-y-4">
            {field("Μάθημα", (
              <select data-testid="studio-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="input-cream w-full">
                <option value="">— Γενικό (πλατφόρμα) —</option>
                {(targets?.courses || []).map((c) => (
                  <option key={c.slug} value={c.slug}>{c.title} · {c.offer_price ? `${c.offer_price}€ (προσφορά)` : `${c.price}€`}</option>
                ))}
              </select>
            ))}
            <div className="grid sm:grid-cols-2 gap-4">
              {field("Κανάλι", (
                <select data-testid="studio-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="input-cream w-full">
                  {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {field("Ύφος", (
                <select data-testid="studio-tone" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} className="input-cream w-full">
                  {TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {field("Γλώσσα", (
                <select data-testid="studio-lang" value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className="input-cream w-full">
                  <option value="el">Ελληνικά</option>
                  <option value="en">English</option>
                </select>
              ))}
              {field("Μέγεθος κάρτας", (
                <select data-testid="studio-card-kind" value={cardKind} onChange={(e) => setCardKind(e.target.value)} className="input-cream w-full">
                  {CARDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
            </div>
            {field("Οδηγίες / σημειώσεις (προαιρετικά)", (
              <textarea data-testid="studio-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="π.χ. έμφαση στο πιστοποιητικό, κοινό φυσιοθεραπευτές, ανακοίνωση νέας ενότητας" className="input-cream w-full" />
            ))}
            <div className="flex flex-wrap gap-3">
              <button data-testid="studio-generate" disabled={busy} onClick={generate} className="btn-blue inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Παραγωγή copy
              </button>
              <button data-testid="studio-card" disabled={busy || !out} onClick={makeCard} className="btn-outline inline-flex items-center gap-2 disabled:opacity-50">
                <ImageIcon className="w-4 h-4" /> Φτιάξε κάρτα
              </button>
            </div>
            {selected && <p className="text-xs text-[#64748B]">{selected.category} · {selected.hours}h · {selected.offer_price ? `σήμερα ${selected.offer_price}€ αντί ${selected.price}€` : `${selected.price}€`}</p>}
          </div>

          <div data-testid="studio-output">
            {!out ? (
              <div className="card-dark p-10 text-center text-[#64748B] h-full flex items-center justify-center">
                Διάλεξε μάθημα και κανάλι και πάτα «Παραγωγή copy».
              </div>
            ) : (
              <>
                {section("Hook", out.hook, out.hook)}
                {section("Caption", out.caption, `${out.caption}\n\n${(out.hashtags || []).join(" ")}`)}
                {section("Hashtags", (out.hashtags || []).join(" "), (out.hashtags || []).join(" "))}
                {section("CTA", (out.ctas || []).map((c) => `• ${c}`).join("\n"), (out.ctas || []).join("\n"))}
                {section("Carousel", (out.carousel || []).map((s, i) => `${i + 1}. ${s.title}\n${s.text}`).join("\n\n"),
                  (out.carousel || []).map((s, i) => `${i + 1}. ${s.title}\n${s.text}`).join("\n\n"))}
                {section("Story / Reel", out.story, out.story)}
                {out.visual && (
                  <div className="card-dark p-5 mt-4" data-testid="studio-ready-post">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">Έτοιμο post</p>
                    <p className="text-xs text-[#64748B] mt-1">Προτεινόμενη εικόνα για αυτό το μάθημα — κατέβασέ την και ανέβασέ τη με το κείμενο.</p>
                    <div className="grid sm:grid-cols-[1fr_120px] gap-4 mt-4 items-start">
                      <img src={out.visual.post} alt="post visual" className="w-full rounded-xl border border-[#E2E8F0]" data-testid="studio-visual-post" />
                      <img src={out.visual.story} alt="story visual" className="w-full rounded-xl border border-[#E2E8F0]" data-testid="studio-visual-story" />
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4">
                      <a href={out.visual.post} download className="btn-blue inline-flex !px-5 !py-2 text-xs" data-testid="studio-download-post">Εικόνα post</a>
                      <a href={out.visual.story} download className="btn-outline inline-flex !px-5 !py-2 text-xs" data-testid="studio-download-story">Εικόνα story 9:16</a>
                      <button
                        onClick={() => copy(`${out.caption}\n\n${(out.hashtags || []).join(" ")}`, "Κείμενο + hashtags")}
                        className="btn-outline inline-flex !px-5 !py-2 text-xs"
                        data-testid="studio-copy-ready-post"
                      >
                        Αντιγραφή κειμένου + hashtags
                      </button>
                    </div>
                  </div>
                )}
                {card && (
                  <div className="card-dark p-5 mt-4" data-testid="studio-card-preview">
                    <img src={card} alt="card" className="w-full rounded-xl" />
                    <a href={card} download={cardName} className="btn-blue inline-flex mt-4 !px-5 !py-2 text-xs" data-testid="studio-card-download">Κατέβασε την κάρτα</a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {history.length > 0 && (
          <>
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A] mt-14 flex items-center gap-2"><History className="w-5 h-5 text-[#1D4ED8]" /> Ιστορικό</h2>
            <div className="card-dark mt-5 divide-y divide-[#E2E8F0]" data-testid="studio-history">
              {history.map((h) => (
                <div key={h.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`studio-history-${h.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3A] text-sm">{h.course_title || "Γενικό"} · {h.platform}</p>
                    <p className="text-xs text-[#64748B] truncate">{h.hook}</p>
                  </div>
                  <button onClick={() => setOut(h)} className="px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#E2E8F0] text-[#334155] hover:border-[#1D4ED8]">Άνοιξε</button>
                  <button onClick={() => removeItem(h.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
